<template>
<div>
  <!-- Details List -->
  <form-accordion :title="ServiceDetails" v-if="showMainDetails">
    <b-row>
      <b-col sm="6" class="form-group">
        <form-read-group-label-input
          :idLabel="$t('common.rsn')" :inputValue="payment.reason">
        </form-read-group-label-input>
      </b-col>
      <b-col sm="6" class="form-group">
        <form-read-group-label-input
          :idLabel="$t('common.idtfr')" :inputValue="payment.endToEndId">
        </form-read-group-label-input>
      </b-col>
    </b-row>
    <!-- TLV 25/09/2020 : On affiche le nom et l'e-mail du créancier si c'est le créancier
    (le destinataire du DP) qui regarde. Si le débiteur (l'expéditeur du DP)
    regarde, on affiche le nom et l'e-mail du débiteur -->
    <b-row>
      <template v-if="payment.direction === '1'">
        <b-col sm="6" class="form-group">
          <form-read-group-label-input
            :idLabel="$t('common.crdtr_name')"
            :inputValue="payment.creditorName">
          </form-read-group-label-input>
        </b-col>
        <b-col sm="6" class="form-group">
          <form-read-group-label-input
            :idLabel="$t('common.crdtr_mail')"
            :inputValue="payment.creditorIdentifier">
          </form-read-group-label-input>
        </b-col>
      </template>
      <template v-else-if="payment.direction === '0'">
        <b-col sm="6" class="form-group">
          <form-read-group-label-input
            :idLabel="$t('common.debtor_name')"
            :inputValue="payment.debtorName">
          </form-read-group-label-input>
        </b-col>
        <b-col sm="6" class="form-group">
          <form-read-group-label-input
            :idLabel="$t('common.debtor_email')"
            :inputValue="payment.debtorIdentifier">
          </form-read-group-label-input>
        </b-col>
      </template>
    </b-row>

    <b-row>
      <!-- Affichage IBAN, ou Sort Code & accountNumber, etc -->
      <template
        v-for="accountData in bankLabelHidden(payment.creditorBankAccount, payment.creditorZoneCode)">
        <b-col sm="6" class="form-group">
          <form-read-group-label-input :key="accountData.hiddenValue"
            :idLabel="payment.direction === '1' ? $t(accountData.labelId + '_creditor') : $t(accountData.labelId + '_my')"
            :inputValue="accountData.hiddenValue">
          </form-read-group-label-input>
        </b-col>
      </template>
      <template
        v-for="debtorAccountData in bankLabelHidden(payment.debtorBankAccount, payment.debtorZoneCode)">
        <b-col sm="6" class="form-group">
          <form-read-group-label-input :key="debtorAccountData.hiddenValue"
            :idLabel="payment.direction === '0' ? $t(debtorAccountData.labelId + '_debtor') : $t(debtorAccountData.labelId + '_my')"
            :inputValue="debtorAccountData.hiddenValue">
          </form-read-group-label-input>
        </b-col>
      </template>
    </b-row>

    <b-row>
      <b-col sm="6" class="form-group">
        <form-read-group-label-input
          idLabel="Date de création du paiement" :inputValue="payment.creationDate | formatDate">
        </form-read-group-label-input>
      </b-col>
      <b-col sm="6" class="form-group">
        <form-read-group-label-input
          :idLabel="$t('common.status.status_label')"
          :inputValue="getStatusLabel(payment.status, payment.direction)"
          :statusIcon="getIcon(payment.status, '0')">
        </form-read-group-label-input>
      </b-col>
    </b-row>

    <b-row>
      <b-col sm="6" class="form-group">
        <form-read-group-label-input
          :idLabel="$t('common.total_amount_no_currency')"
          :inputValue="payment.amount + ' ' + getCurrencyIcon(payment.currency)">
        </form-read-group-label-input>
      </b-col>
      <b-col sm="6" class="form-group">
        <form-read-group-label-input
          idLabel="Date d'exécution" :inputValue="payment.requestedExecutionDate | formatDate">
        </form-read-group-label-input>
      </b-col>
      <b-col sm="6" class="form-group"
          v-if="payment.attachmentId !== ''">
        <button round
          @click="view(payment.attachmentId)"
          class="button-action milkyBlockButton"
          v-if="ifAndroid">
          <i class="icon fa fa-paperclip"></i>
          {{ $t('creation_forms.view_attchmnt') }}
        </button>
        <button round
          v-if="!ifAndroid"
          @click="view(payment.attachmentId)"
          class="button-action milkyBlockBlack">
            <i class="icon fa fa-paperclip"></i>
            {{ $t('creation_forms.dwnld_attchmnt') }}
        </button>
      </b-col>

    </b-row>

    <b-row class="mt-2" v-if="[status.MADE, status.INST_MADE].includes(payment.status)">
      <b-col cols="12" class="transactionDetailSubHeader">
        {{ "Lettrage" }}
      </b-col>
      <b-col cols="12" class="mt-2">
        {{ payment.lettrage && payment.lettrage.length > 0 ? formatLettrage(payment.lettrage) : 'Pas de lettrage' }}
      </b-col>
    </b-row>
  </form-accordion>

  <hr v-if="showMainDetails"/>

  <!-- History -->
  <form-accordion :title="$t('common.formsSubTitle.dir_pymnt_hstry')" v-if="payment.history.length > 0">
    <div class="form-group row col-sm-6" id="inst-payment-opt">
      <b-form-checkbox
        v-model="payment.instantPayment"
        value="1"
        unchecked-value="0"
        disabled>
        {{ $t('requestToPay.is_inst_payment') }}
      </b-form-checkbox>
    </div>
    <div class="form-group row">
      <div class="col-12">
        <table class="table table-striped table-bordered table-status-history">
          <thead class="thead-dark">
            <tr>
              <th scope="col" class='reponsiveColumnWidth'>{{ $t('common.date_label') }}</th>
              <th scope="col">{{ $t('common.status.status_label') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="record in payment.history" :key="record.id">
              <td class="dateTimehistoryHeight">{{record.creationDate | formatDateTime}}</td>
              <td>
                <div class="row">
                  <div class="col-sm-7 statusHistoryLabel">
                    {{getStatusLabel(record.status, payment.direction)}}
                    <i :class="'icon icon-transaction-detail ' + getIcon(record.status)"></i>
                  </div>
                  <div class="col-sm-5">
                    <f7-button
                      class="xmlButtonBackground"
                      @click="viewXML()"
                      v-if="showPain001View(record)">
                      {{ $t('common.button_technical_details') }}
                    </f7-button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </form-accordion>
</div>
</template>

<script>
import FormAccordion from '@/components/common/FormAccordion.vue'
import formReadGroupLabelInput from '@/components/common/formReadGroupLabelInput.vue'
import status from '@/helpers/status'
import {statusLabel, statusIcon} from '@/helpers/labels'
import {errorMessageAlert} from '@/helpers/alert'
import {extractForeignIban, formatLettrage} from '@/helpers/format'
import moment from 'moment'
import b64toBlob from 'b64-to-blob'

export default {
  name: 'DPdetailsCapsule',
  props: ['payment', 'showMainDetails'],
  components: {
    'form-accordion': FormAccordion,
    'form-read-group-label-input': formReadGroupLabelInput
  },
  data () {
    return {
      colorBottomButton: this.$engine.services.getButtonClassColorById(this.$engine.user.currentService.id),
      ServiceDetails: '',
      status: status
    }
  },
  created () {
    this.ServiceDetails = this.$i18n.t('common.item_details',
      {itemname: this.$i18n.t(this.$engine.services.getSingularNameFromServiceId(this.$engine.user.currentService.id))})
  },
  computed: {
    ifAndroid () {
      return this.$f7.device.os === 'android'
    }
  },
  methods: {
    formatLettrage: formatLettrage,
    showPain001View (record) {
      return (record.status === status.ORDER_GENERATED || record.status === status.INST_ORDER_GENERATED)
    },
    getStatusLabel (status, direction) {
      return statusLabel(this.$i18n, status, -1, direction)
    },
    getIcon (status, negotStatus) {
      return statusIcon(status, negotStatus)
    },
    view: function (id) {
      // Get cloudDocument
      // var resource = 'attachment/detail/' + id
      const that = this
      this.$f7.showPreloader('Téléchargement du fichier')
      this.$engine.services.payments.getAttachmentDetail(id)
        .then(response => {
          this.$engine.settings.set({Attachment: {
            data: response.data.documentData,
            name: response.data.documentName,
            mimeType: response.data.documentData.split(';')[0].replace(/^data:/, ''),
            id: id
          }})
          if (that.$f7.device.os === 'android' || that.$f7.device.os === 'ios') {
            that.writeToFile(
              response.data.documentName,
              response.data.documentData,
              response.data.documentData.split(';')[0].replace(/^data:/, ''),
              true,
              function (filePath, fileType, fileName) {
                that.readFile(filePath, fileType, fileName)
              },
              function () {
                that.$f7.alert('', 'Error creating the file')
              }
            )
          } else {
            let blob = b64toBlob(response.data.documentData.split(',')[1], response.data.documentData.split(';')[0].replace(/^data:/, ''))
            const url = window.URL.createObjectURL(blob)
            const fileLink = document.createElement('a')
            fileLink.href = url
            fileLink.setAttribute('download', response.data.documentName) // or any other extension
            fileLink.click()
          }
          setTimeout(function () { that.$f7.hidePreloader() }, 1000)
        })
        .catch(error => {
          that.$f7.hidePreloader()
          errorMessageAlert(error, that)
          // errorMessageAlert(error, that)
        })
    },
    viewXML: function () {
      const that = this
      this.$f7.showPreloader(that.$i18n.t('common.opnng_fl'))
      // let userId = that.$engine.user.get().id
      // this.$engine.services.getEnvelope(idMessage, typeMessage, userId)
      this.$engine.services.getXMLDetail('directPayment', 'pain001', this.payment.endToEndId)
        .then(response => {
          that.$f7.hidePreloader()
          this.$engine.settings.set({type: {
            data: response.data
          }})
          /* TLV 22/09/2020
          * Lignes ci-dessous a decommenter quand on testera le telechargement de
          * fichiers sur mobile */
          /* if (that.$f7.device.os === 'android' || that.$f7.device.os === 'ios') {
            that.writeToFile(
              typeMessage + '_' + idMessage + '.xml',
              response.data.data,
              response.data.data.split(';')[0].replace(/^data:/, ''),
              true,
              function (filePath, fileType, fileName) {
                that.readFile(filePath, fileType, fileName)
              },
              function () {
                that.$f7.alert('', 'Error creating the file')
              }
            )
          } else { */ // browser
          /* let blob = base64url.decode(response.data['envelopes'][0].message)
          const linkSource = 'data:text/csv;charset:utf-8,' + blob */
          let blob = b64toBlob(response.data.data.split(',')[1], 'text/xml')
          const linkSource = window.URL.createObjectURL(blob)
          const fileLink = document.createElement('a')
          fileLink.href = linkSource
          let today = moment().format()
          fileLink.setAttribute('download', 'Technical_View' + today + '.xml') // or any other extension
          fileLink.click()
          // }
        })
        .catch(error => {
          that.$f7.hidePreloader()
          console.log(error)
        })
    },
    writeToFile (fileName, data, fileType, readonly, success, error) {
      /* eslint-disable no-undef */
      let that = this
      let directory = ''

      if (readonly) {
        if (that.$f7.device.os === 'android') {
          directory = cordova.file.externalDataDirectory
        } else if (that.$f7.device.os === 'ios') {
          directory = cordova.file.tempDirectory
        } else {
          directory = cordova.file.dataDirectory
        }
        that.saveFileToDirectory(fileName, data, fileType, directory, success, error)
      } else {
        // directory = cordova.file.externalRootDirectory
        if (that.$f7.device.os === 'android') {
          directory = cordova.file.externalRootDirectory
        } else if (that.$f7.device.os === 'ios') {
          // directory = cordova.file.dataDirectory
          directory = cordova.file.tempDirectory
        } else {
          directory = cordova.file.dataDirectory
        }
        try {
          that.checkForWritePermissions(function () {
            try {
              window.resolveLocalFileSystemURL(directory, function (directoryEntry) {
                directoryEntry.getDirectory('Download', {create: true}, function (downloadDir, _data) {
                  that.saveFileToDirectory(fileName, data, fileType, downloadDir.nativeURL, function (filePath, fileType) {
                    console.log('sucess saving file: ', filePath)
                  }, function (error, _data) {
                    console.error('error on saving file' + error.code)
                    alert('Unable to save file: ' + error.code)
                  })
                }, function (error, _data) {
                  console.error('error on accessing download directory' + error.code)
                  alert('Unable to get/create download directory: ' + error.code)
                })
              }, (e, file) => { // error resolveLocalFileSystemURL
              })
            } catch (error) { // get dir
              console.error(error)
            }
          }, function () {
            alert('App needs access to the phone\'s file storage in order to save files.')
          })
        } catch (error) { // get perms
          console.error(error)
        }
      }
      /* eslint-enable no-undef */
    },
    checkForWritePermissions (sucess, error) {
      /* eslint-disable no-undef */
      if (this.$f7.device.os === 'android') {
        cordova.plugins.diagnostic.requestRuntimePermission(function (status) {
          switch (status) {
            case cordova.plugins.diagnostic.permissionStatus.GRANTED:
              console.log('Permission granted')
              sucess()
              break
            case cordova.plugins.diagnostic.permissionStatus.DENIED:
              console.log('Permission denied')
              error()
              break
            case cordova.plugins.diagnostic.permissionStatus.DENIED_ALWAYS:
              console.log('Permission permanently denied')
              error()
              break
          }
        }, function (error) {
          console.error('The following error occurred: ' + error)
        },
          cordova.plugins.diagnostic.permission.WRITE_EXTERNAL_STORAGE
        )
      } else if (this.$f7.device.os === 'ios') {
        sucess()
      }
      /* eslint-enable no-undef */
    },
    saveFileToDirectory (fileName, data, fileType, directory, success, error) {
      /* eslint-disable no-undef */
      let directoryEntryReference = null
      window.resolveLocalFileSystemURL(directory, function (directoryEntry) {
        directoryEntryReference = directoryEntry
        directoryEntry.getFile(fileName, { create: true, exclusive: true }, function (fileEntry) {
          fileEntry.createWriter(function (fileWriter) {
            fileWriter.onwriteend = function (e) {
              success(e.target.localURL, fileType, fileName)
            }
            fileWriter.onerror = function (e) {
              error()
            }
            let blob = b64toBlob(data.replace(/^data:\w+\/\w+;base64,/, ''), fileType)
            fileWriter.write(blob)
          }, (e, file) => {
          })
        }, (e, file) => {
          navigator.notification.confirm('This file already exists do you want to override it ? (' + fileName + ')', function (index) {
            console.log('Do you want to override the following file: ' + fileName + ' (' + index + ')')
            switch (index) {
              case 1: // No
                console.log('No. Returning to idle state.')
                break
              case 2: // Yes -> overrides  the file
                console.log('Yes. Overriding file.')
                directoryEntryReference.getFile(fileName, { create: true, exclusive: false }, function (fileEntry) {
                  fileEntry.createWriter(function (fileWriter) {
                    fileWriter.onwriteend = function (e) {
                      success(e.target.localURL, fileType, fileName)
                    }
                    fileWriter.onerror = function (e) {
                      error()
                    }
                    let blob = b64toBlob(data.replace(/^data:\w+\/\w+;base64,/, ''), fileType)
                    fileWriter.write(blob)
                  }, (e, file) => {
                  })
                }, (e, file) => {
                })
                break
            }
          }, 'Information', ['No', 'Yes'])
        })
      }, (e, file) => { // error resolveLocalFileSystemURL
      })
      /* eslint-enable no-undef */
    },
    readFile (filePath, fileType, fileName) {
      /* eslint-disable no-undef */
      // TODO : test fileType before trying to open and let the user know
      window.resolveLocalFileSystemURL(filePath, function (entry) {
        cordova.plugins.fileOpener2.open(entry.nativeURL.replace(/%20/g, ' '), fileType, {
          error: function (e) {
            // TODO : screen user stuff
          },
          success: function (s) {
            // TODO : screen user stuff
          }
        })
      })
    },
    getCurrencyIcon (ccy) {
      switch (ccy) {
        case 'EUR':
          return '€'
        case 'USD':
          return '$'
        case 'AUD':
          return 'A$'
        case 'GBP':
          return '£'
        case 'JPY':
          return '¥'
        case 'RUB':
          return '₽'
        default:
          return ccy
      }
    },
    bankLabelHidden (iban, zone) {
      return extractForeignIban(iban, zone)
    }
  }
}
</script>

<style>
#inst-payment-opt > .custom-control.custom-checkbox > label {
  cursor: default;
}
</style>



// WEBPACK FOOTER //
// DPdetailsCapsule.vue?0bbe052b